import React from "react";
import styled, { keyframes } from "styled-components";
import { transparentize } from "polished";

import { normalBarlineWidth } from "../utils";
import { IToneInProps, IModifierProps, IBarlineProps, IDotsBeforeProps, IDotProps } from "./interfaces";

export const StyledBar = styled.div`
    display: flex;
`;

export const Note = styled.div`
    position: relative;
`;

export const Tone = styled.div`
    position: absolute;
    width: 100%;
`;

const fader = keyframes`
    0% {
        opacity: 0.75;
        transform: scale(1);
    }
    50% {
        opacity: 0.45;
        transform: scale(0.97);
    }
    100% {
        opacity: 0.75;
        transform: scale(1);
    }
`;

export const ToneIn = styled.div<IToneInProps>`
    background: ${p => transparentize(1 - (p.isChristmas ? 0.6 : 0.5), p.isLow ? "#d0eae6" : "white")};
    margin: 2px;
    height: calc(100% - 4px);
    border-radius: 5px;
    cursor: pointer;
    border: solid 2px black;
    box-sizing: border-box;

    &.ToneDisabled {
        background-color: transparent;
        cursor: default;
        pointer-events: none;
        border: none;
    }
    &.Selected {
        opacity: 1;
        cursor: pointer;
        pointer-events: all;
    }

    &.Incorrect {
        background: ${p => p.theme.c.incorrect} !important;
        border: solid 2px black;
    }

    &.Correct {
        background: ${p => p.theme.c.correct} !important;
        border: solid 2px black;
    }

    &.PlayingNote {
        background: ${p => transparentize(1 - 0.4, p.theme.c.play)} !important;
    }

    &.PlayingNote.Selected {
        background: ${p => transparentize(1 - 0.6, p.theme.c.play)} !important;
    }

    &.CheckingPlayNote {
        background: ${p => transparentize(1 - 0.4, p.theme.c.check)} !important;
    }

    &.CheckingPlayNote.Selected {
        background: ${p => p.theme.c.check} !important;
    }

    &.Rest {
        background-color: ${p => transparentize(0.4, p.theme.c.disabled)};
        pointer-events: none;
    }

    &.Passive {
        background-color: transparent;
        pointer-events: none;
        border: none;
    }

    &.Ledger {
        &:after {
            content: "";
            width: calc(100% - 4px);
            position: absolute;
            top: calc(50% - 2px);
            left: 1px;
            height: 4px;
            background-color: ${p => p.theme.c.blackShadow35};
        }
    }

    &.Fader {
        animation: ${fader} infinite 2.4s;
        [class*="Modifier"] {
            position: relative;
            margin-top: -4px;
            margin-left: -4px;
        }
    }

    &.Tied {
        &:before {
            content: "";
            width: ${p => (p.isLastNoteOfBar ? 12 : 4)}px;
            background-color: ${p =>
                p.color
                    ? p.color === "Correct"
                        ? p.theme.c.correct
                        : p.color === "Incorrect"
                        ? p.theme.c.incorrect
                        : p.color
                    : p.theme.c.disabled};
            opacity: 1;
            position: absolute;
            left: calc(100% - 2px);
            top: 6px;
            bottom: 6px;
        }
    }

    &.TiedOff {
        pointer-events: none;
    }
`;

export const Modifier = styled.div<IModifierProps>`
    span {
        position: absolute;
        opacity: 0.5;
        z-index: 10;
        top: ${p => p.scale / 4 - (p.scale / 4) * 5.7}px;
        left: ${p => (-p.scale / 1.2) * (p.isDoubleFlat ? 2 : 1)}px;
        font-size: ${p => ((p.scale / 100) * 80) / 0.295}px;
        color: black;
        pointer-events: none;
    }
`;

export const Barline = styled.div<IBarlineProps>`
    height: 100%;
    width: ${p => normalBarlineWidth(p.width)}px;
    background-color: ${p => p.isChristmas ? p.theme.c.christmasBlue : "black"};;
`;

export const BoldBarline = styled.div<IBarlineProps>`
    height: 100%;
    width: ${p => (p.width / 100) * 6 * 2.5}px;
    background-color: ${p => p.isChristmas ? p.theme.c.christmasBlue : "black"};;
`;

export const BarlineGap = styled.div<IBarlineProps>`
    width: ${p => (p.width / 100) * 8}px;
`;

export const Flex = styled.div`
    display: flex;
`;

export const DoubleBarline = ({ width, isChristmas }: IBarlineProps) => (
    <Flex>
        <Barline width={width} isChristmas={isChristmas} />
        <BarlineGap width={width} isChristmas={isChristmas} />
        <Barline width={width} isChristmas={isChristmas} />
    </Flex>
);

export const FinalBarline = ({ width, isChristmas }: IBarlineProps) => (
    <Flex>
        <Barline width={width} isChristmas={isChristmas} />
        <BarlineGap width={width} isChristmas={isChristmas} />
        <BoldBarline width={width} isChristmas={isChristmas} />
    </Flex>
);

export const FinalStartBarline = ({ width, isChristmas }: IBarlineProps) => (
    <Flex>
        <BoldBarline width={width} isChristmas={isChristmas} />
        <BarlineGap width={width}isChristmas={isChristmas}  />
        <Barline width={width} isChristmas={isChristmas} />
    </Flex>
);

export const FinalBothBarline = ({ width, isChristmas }: IBarlineProps) => (
    <Flex>
        <Barline width={width} isChristmas={isChristmas} />
        <BarlineGap width={width} isChristmas={isChristmas} />
        <BoldBarline width={width} isChristmas={isChristmas} />
        <BarlineGap width={width} isChristmas={isChristmas} />
        <Barline width={width} isChristmas={isChristmas} />
    </Flex>
);

const StyledDots = styled.div`
    position: relative;
`;

const Dot = styled.div<IDotProps>`
    position: absolute;
    background-color: ${p => p.isChristmas ? p.theme.c.christmasBlue : "black"};
`;

const DotUp = styled(Dot)``;

const DotDown = styled(Dot)``;

export const DotsBefore = ({ style, size, playing, isChristmas }: IDotsBeforeProps) => {
    const dotDim = (size.y * size.x) / 100 / 4;
    const dotWidth = ((size.y * size.x) / 1000) * 4;
    return (
        <StyledDots
            style={{
                ...style,
                width: dotWidth,
            }}
        >
            {!playing && (
                <>
                    <DotUp
                        style={{
                            borderRadius: dotDim / 2,
                            width: dotDim,
                            height: dotDim,
                            marginTop: -dotDim / 2,
                            marginLeft: -dotDim / 2,
                            top: -size.y / 2,
                            left: dotWidth / 2,
                        }}
                        isChristmas={isChristmas}
                    />
                    <DotDown
                        style={{
                            borderRadius: dotDim / 2,
                            width: dotDim,
                            height: dotDim,
                            marginTop: -dotDim / 2,
                            marginLeft: -dotDim / 2,
                            top: size.y / 2,
                            left: dotWidth / 2,
                        }}
                        isChristmas={isChristmas}
                    />
                </>
            )}
        </StyledDots>
    );
};
